import React, { useState } from 'react'
import ModalImage from 'react-modal-image'
import { getGalleryImageUrl, getGalleryImageAlt } from '../helpers/galleryHelpers'

const Gallery = ({ property }) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [modalIndex, setModalIndex] = useState(null)

  // Number of images to show per page
  const imagesPerPage = 3

  const isSingleImage = property.gallery.length === 1

  // Reduce height on mobile while keeping larger sizes on bigger screens
  const fixedImageHeight = isSingleImage ? 'h-56 sm:h-[520px] lg:h-[600px]' : 'h-48 sm:h-[360px] lg:h-[440px]'

  const totalPages = Math.ceil(property.gallery.length / imagesPerPage)

  const openModal = (index) => {
    setModalIndex(index)
  }

  const closeModal = () => {
    setModalIndex(null)
  }

  const showNextPage = () => {
    setCurrentPage((currentPage + 1) % totalPages)
  }

  const showPrevPage = () => {
    setCurrentPage((currentPage - 1 + totalPages) % totalPages)
  }

  const showNextModal = () => {
    setModalIndex((modalIndex + 1) % property.gallery.length)
  }

  const showPrevModal = () => {
    setModalIndex((modalIndex - 1 + property.gallery.length) % property.gallery.length)
  }

  const modalRef = React.useRef(null)

  React.useEffect(() => {
    if (modalIndex !== null && modalRef.current) {
      modalRef.current.focus()
    }
  }, [modalIndex])

  const getCurrentPageImages = () => {
    const startIndex = currentPage * imagesPerPage
    return property.gallery.slice(startIndex, startIndex + imagesPerPage)
  }

  return (
    <div className="mb-6">
      {/* Carousel Container */}
      <div className="relative">
        {/* Images Container - centered with constrained width */}
        <div className="flex gap-4 overflow-hidden justify-center">
          {getCurrentPageImages().map((image, index) => {
            const globalIndex = currentPage * imagesPerPage + index
            return (
              <div key={globalIndex} className={isSingleImage ? 'w-full max-w-none' : 'w-1/3 max-w-2xl'}>
                <div className={`${fixedImageHeight} relative`}>
                  <img
                    src={getGalleryImageUrl(property.gallery, globalIndex)}
                    alt={getGalleryImageAlt(property.gallery, globalIndex) || `Property ${globalIndex + 1}`}
                    className="object-cover w-full h-full cursor-pointer rounded-lg"
                    onClick={() => openModal(globalIndex)}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {/* Navigation Arrows */}
        {totalPages > 1 && (
          <>
            <button
              onClick={showPrevPage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
              aria-label="Previous page"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={showNextPage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
              aria-label="Next page"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Dots Navigation */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index)}
              className={`w-3 h-3 rounded-full transition-all ${currentPage === index ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'}`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {modalIndex !== null && (
        <div
          ref={modalRef}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50"
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              showPrevModal()
            } else if (e.key === 'ArrowRight') {
              showNextModal()
            } else if (e.key === 'Escape') {
              closeModal()
            }
          }}
          tabIndex="0"
          role="button"
          aria-label="Image navigation"
        >
          <div className="absolute inset-0" onClick={closeModal}></div>
          <button onClick={showPrevModal} className="absolute left-4 p-4 text-white z-10 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75 transition-all">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <ModalImage
            small={getGalleryImageUrl(property.gallery, modalIndex)}
            large={getGalleryImageUrl(property.gallery, modalIndex)}
            alt={getGalleryImageAlt(property.gallery, modalIndex) || `Property ${modalIndex + 1}`}
            onClose={closeModal}
          />
          <button onClick={showNextModal} className="absolute right-4 p-4 text-white z-10 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75 transition-all">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

export default Gallery
